<template>
  <div class="attendee-card">
    <div class="attendee-card-header">
      <div class="attendee-card-title">
        <template v-if="totalSlots > 1">
          Ticket {{ index + 1 }} <span class="ticket-name-tag">{{ ticketName }}</span>
        </template>
        <template v-else>{{ ticketName }}</template>
      </div>
      <span v-if="isPurchaser && totalSlots > 1" class="purchaser-badge">You · Purchaser</span>
    </div>

    <div v-if="!isPurchaser" class="card-note">
      <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
      <span>{{ noteText }}</span>
    </div>

    <lp-form-field
      v-for="f in visibleFields"
      :key="f.name"
      :field="f"
      :value="values[f.name]"
      :values="values"
      :scope="index"
      @input="onInput(f, $event)"
      @blur="onBlur(f)"
    />

    <template v-if="showAssignment">
      <div class="attendee-section-title">Add-ons assigned to this attendee</div>
      <div class="addon-assign-list">
        <label
          v-for="a in cartedAddons"
          :key="a.id"
          class="addon-assign-row"
          :class="{ checked: isAssigned(a), disabled: rowDisabled(a) }"
        >
          <input
            type="checkbox"
            :checked="isAssigned(a)"
            :disabled="rowDisabled(a)"
            @change="$emit('toggle-assign', { addonId: a.id, slotIdx: index })"
          />
          <div class="addon-assign-info">
            <div class="addon-assign-name">{{ a.name }}</div>
            <div class="addon-assign-desc">{{ a.desc }}</div>
          </div>
          <div class="addon-assign-meta">
            <div class="addon-assign-price">${{ a.price }}</div>
            <div class="addon-assign-status">{{ assignedCounts[a.id] || 0 }} of {{ addonQty[a.id] }} assigned</div>
          </div>
        </label>
      </div>
    </template>
  </div>
</template>

<script>
const PURCHASER_ONLY_MINIMAL = ['first_name', 'last_name', 'email', 'email_confirmation']

export default {
  name: 'lp-attendee-card',
  props: {
    index: { type: Number, required: true },
    ticketName: { type: String, default: '' },
    isPurchaser: { type: Boolean, default: false },
    totalSlots: { type: Number, default: 1 },
    // Form-builder JSON schema (from the API).
    schema: { type: Array, default: () => [] },
    // This attendee's field values, keyed by field name (owned by the page).
    values: { type: Object, required: true },
    // Names of fields whose value change should trigger a coupon check.
    fieldsWithCoupons: { type: Array, default: () => [] },
    // Whether sessions are actually picked during registration (agenda groups).
    sessionsSelectable: { type: Boolean, default: false },
    // Add-on assignment context (only for multi-attendee orders).
    showAssignment: { type: Boolean, default: false },
    cartedAddons: { type: Array, default: () => [] },
    addonAssignments: { type: Object, default: () => ({}) },
    addonQty: { type: Object, default: () => ({}) },
    assignedCounts: { type: Object, default: () => ({}) },
  },
  computed: {
    visibleFields() {
      if (this.isPurchaser) return this.schema
      // Additional attendees only supply identity; they complete the rest later.
      return this.schema.filter(f => PURCHASER_ONLY_MINIMAL.includes(f.name))
    },
    noteText() {
      const base = 'This person will receive an email to complete their profile (address, license info)'
      // Only mention session selection when it actually happens during registration.
      return this.sessionsSelectable
        ? `${base} and pick their own sessions.`
        : `${base}.`
    },
  },
  methods: {
    onInput(field, value) {
      this.$emit('field-input', { index: this.index, name: field.name, value })
      if (this.fieldsWithCoupons.includes(field.name)) {
        this.$emit('field-coupon', { index: this.index, name: field.name, value })
      }
    },
    onBlur(field) {
      if (field.name === 'email') {
        this.$emit('email-blur', { index: this.index, email: this.values.email })
      }
    },
    isAssigned(a) {
      return (this.addonAssignments[a.id] || []).includes(this.index)
    },
    rowDisabled(a) {
      const remaining = (this.addonQty[a.id] || 0) - (this.assignedCounts[a.id] || 0)
      return !this.isAssigned(a) && remaining <= 0
    },
  },
}
</script>

<style lang="scss">
.attendee-card {
  background: var(--surface);
  border: 0.5px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 28px 32px;
}
.attendee-card + .attendee-card { margin-top: 16px; }
.attendee-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 0.5px solid var(--border);
}
.attendee-card-title {
  font-size: var(--fs-lead);
  font-weight: 500;
  color: var(--brand-deep);
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.ticket-name-tag {
  font-size: var(--fs-small);
  font-weight: 400;
  color: var(--text-secondary);
  background: var(--surface-alt);
  padding: 3px 10px;
  border-radius: var(--radius-sm);
}
.purchaser-badge {
  font-size: var(--fs-caption);
  font-weight: 500;
  background: var(--brand);
  color: white;
  padding: 3px 10px;
  border-radius: 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}
.card-note {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  background: var(--brand-soft);
  border-radius: var(--radius-sm);
  padding: 10px 12px;
  font-size: var(--fs-label);
  line-height: 1.5;
  color: var(--brand-deep);
  margin-bottom: 18px;
}
.card-note svg { flex-shrink: 0; margin-top: 1px; }
.addon-assign-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.addon-assign-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
  background: var(--surface-alt);
  border: 0.5px solid var(--border);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
}
.addon-assign-row:hover:not(.disabled) {
  background: var(--bg);
  border-color: var(--brand-light);
}
.addon-assign-row.checked {
  background: var(--brand-soft);
  border-color: var(--brand);
}
.addon-assign-row.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.addon-assign-row input { width: 18px; height: 18px; flex-shrink: 0; cursor: inherit; }
.addon-assign-info { min-width: 0; }
.addon-assign-name { font-size: var(--fs-small); font-weight: 500; color: var(--text); }
.addon-assign-desc { font-size: var(--fs-label); color: var(--text-secondary); margin-top: 2px; line-height: 1.5; }
.addon-assign-meta { text-align: right; }
.addon-assign-price { font-size: var(--fs-small); font-weight: 500; color: var(--text); }
.addon-assign-status { font-size: var(--fs-caption); color: var(--text-tertiary); margin-top: 2px; white-space: nowrap; }
.attendee-section-title {
  font-size: var(--fs-small);
  font-weight: 500;
  color: var(--text);
  margin: 18px 0 10px;
  padding-bottom: 6px;
  border-bottom: 0.5px solid var(--border);
}
.attendee-section-title:first-child { margin-top: 0; }
</style>
